import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Textarea } from '../ui.jsx';

const STATUS_COLOR = { draft:G.textMuted, sent:G.cyan, viewed:G.purple, signed:G.green };

export function Documents({ toast }) {
  const [tab, setTab] = useState('documents');
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editingTpl, setEditingTpl] = useState(null);
  const [auditFor, setAuditFor] = useState(null); // open audit/signer drawer
  const [signersFor, setSignersFor] = useState(null); // open add-signers modal

  const load = () => Promise.all([
    api('/api/documents'),
    api('/api/documents/templates'),
    api('/api/contacts').catch(()=>[]),
  ]).then(([d,t,c]) => { setDocuments(d); setTemplates(t); setContacts(c); })
   .catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);

  const sendDoc = async d => {
    try { await api(`/api/documents/${d.id}/send`, { method:'POST' }); load(); toast('Sent'); }
    catch(e) { toast(e.message,'error'); }
  };
  const removeDoc = async d => {
    if (!confirm('Delete this document?')) return;
    try { await api(`/api/documents/${d.id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch(e) { toast(e.message,'error'); }
  };
  const removeTpl = async t => {
    if (!confirm('Delete this template?')) return;
    try { await api(`/api/documents/templates/${t.id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch(e) { toast(e.message,'error'); }
  };

  if (loading) return <Spinner sz={32}/>;

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Documents & Agreements</h1>
      <Btn v="primary" onClick={()=> tab==='documents' ? setEditingDoc({ status:'draft', type:'agreement' }) : setEditingTpl({ type:'agreement', isDefault:false })}>
        + New {tab==='documents'?'Document':'Template'}
      </Btn>
    </div>

    <div style={{ display:'flex', gap:6, marginBottom:14, background:G.input, padding:4, borderRadius:10, width:'fit-content' }}>
      <Btn sz="sm" v={tab==='documents'?'primary':'ghost'} onClick={()=>setTab('documents')}>📄 Documents ({documents.length})</Btn>
      <Btn sz="sm" v={tab==='templates'?'primary':'ghost'} onClick={()=>setTab('templates')}>📋 Templates ({templates.length})</Btn>
    </div>

    {tab === 'documents' ? <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>{['Title','Type','Status','Signed','Created',''].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
        <tbody>{documents.map(d => (
          <tr key={d.id} onClick={()=>setEditingDoc(d)} style={{ cursor:'pointer' }}>
            <td style={{ ...styles.td, fontWeight:600 }}>{d.title}</td>
            <td style={styles.td}>{d.type}</td>
            <td style={styles.td}><Badge c={STATUS_COLOR[d.status]} bg={STATUS_COLOR[d.status]+'22'}>{d.status}</Badge></td>
            <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{d.signedAt ? `${d.signedBy} · ${new Date(d.signedAt).toLocaleDateString()}` : '—'}</td>
            <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{new Date(d.createdAt).toLocaleDateString()}</td>
            <td style={styles.td} onClick={e=>e.stopPropagation()}>
              <div style={{ display:'flex', gap:4 }}>
                <Btn sz="xs" v="primary" onClick={()=>setSignersFor(d)}>✍️ Get Signed</Btn>
                <Btn sz="xs" onClick={()=>setAuditFor(d)}>🕓 Audit</Btn>
                <Btn sz="xs" v="danger" onClick={()=>removeDoc(d)}>×</Btn>
              </div>
            </td>
          </tr>
        ))}</tbody>
      </table>
      {documents.length === 0 && <div style={{ padding:48, textAlign:'center', color:G.textMuted }}>No documents yet. Create one from a template.</div>}
    </div>
    : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(360px,1fr))', gap:14 }}>
      {templates.map(t => (
        <div key={t.id} style={styles.card}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:8 }}>
            <div>
              <strong style={{ fontSize:15 }}>{t.title}</strong>
              <Badge c={G.cyan} bg={G.cyanLight} style={{ marginLeft:6 }}>{t.type}</Badge>
              {t.isDefault && <Badge c={G.purple} bg={G.purpleDim} style={{ marginLeft:4 }}>default</Badge>}
            </div>
          </div>
          <div style={{ fontSize:12, color:G.textMuted, marginBottom:10 }}>{(t.variables||[]).length} variables</div>
          <div style={{ display:'flex', gap:6 }}>
            <Btn sz="sm" onClick={()=>setEditingTpl(t)}>Edit</Btn>
            <Btn sz="sm" v="primary" onClick={()=>setEditingDoc({ status:'draft', type:t.type, title:`${t.title} – `, content:t.content, templateId:t.id })}>Use Template</Btn>
            <Btn sz="sm" v="danger" onClick={()=>removeTpl(t)}>×</Btn>
          </div>
        </div>
      ))}
      {templates.length === 0 && <div style={{ ...styles.card, padding:32, textAlign:'center', color:G.textMuted, gridColumn:'1/-1' }}>No templates yet.</div>}
    </div>}

    <DocumentModal doc={editingDoc} contacts={contacts} templates={templates} onClose={()=>setEditingDoc(null)} onSaved={()=>{ load(); setEditingDoc(null); toast('Saved'); }}/>
    <TemplateModal tpl={editingTpl} onClose={()=>setEditingTpl(null)} onSaved={()=>{ load(); setEditingTpl(null); toast('Saved'); }}/>
    {signersFor && <SignersModal doc={signersFor} contacts={contacts} onClose={()=>setSignersFor(null)} onSent={()=>{ setSignersFor(null); load(); toast('✓ Sent for signature'); }} toast={toast}/>}
    {auditFor && <AuditModal doc={auditFor} onClose={()=>setAuditFor(null)} toast={toast}/>}
  </div>;
}

// SignersModal — owner attaches up to N people, optionally uploads a PDF,
// fires per-signer URLs by SMS + email. Each gets a unique link.
function SignersModal({ doc, contacts, onClose, onSent, toast }) {
  const [signers, setSigners] = useState([{ name:'', email:'', phone:'', role:'Customer' }]);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [notify, setNotify] = useState(true);

  // Pre-fill first signer from the linked contact if available
  useEffect(() => {
    const c = doc?.contactId ? contacts.find(x => x.id === doc.contactId) : null;
    if (c) setSigners([{ name:`${c.firstName||''} ${c.lastName||''}`.trim(), email:c.email||'', phone:c.phone||'', role:'Customer' }]);
  }, [doc?.id]);

  const add = () => setSigners(s => [...s, { name:'', email:'', phone:'', role:'' }]);
  const upd = (i, k, v) => setSigners(s => s.map((x,idx) => idx===i ? {...x, [k]:v} : x));
  const rm  = (i) => setSigners(s => s.filter((_,idx) => idx!==i));

  const uploadFile = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const base64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
      await api(`/api/documents/${doc.id}/upload-file`, { method:'POST', body:{ base64, filename: f.name, mime: f.type }});
      setFile({ name: f.name, ok: true });
      toast('✓ File attached');
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const send = async () => {
    const valid = signers.filter(s => s.name && (s.email || s.phone));
    if (!valid.length) return toast('Add at least one signer with name + email or phone', 'error');
    setBusy(true);
    try {
      await api(`/api/documents/${doc.id}/signers`, { method:'POST', body:{ signers: valid, notify }});
      onSent();
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return <Modal open onClose={onClose} width={620}>
    <h2 style={{ margin:'0 0 6px 0', fontSize:18 }}>✍️ Send "{doc.title}" for Signature</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>Each signer gets a unique link. We capture IP, browser, timestamp, and (with permission) location for the audit trail.</div>

    {/* Optional PDF upload */}
    <div style={{ ...styles.card, padding:12, marginBottom:14, background:G.input }}>
      <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>📎 Attach PDF (optional)</div>
      {file ? <div style={{ fontSize:13, color:G.green }}>✓ {file.name}</div> : (
        <label style={{ cursor:'pointer', color:G.cyan, fontSize:13 }}>
          Upload a file
          <input type="file" accept="application/pdf,image/*" style={{ display:'none' }} onChange={(e)=>uploadFile(e.target.files?.[0])}/>
        </label>
      )}
      <div style={{ fontSize:11, color:G.textMuted, marginTop:6 }}>If no file is attached, the signer will see the document text body you wrote.</div>
    </div>

    {signers.map((s, i) => (
      <div key={i} style={{ ...styles.card, padding:12, marginBottom:8 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
          <strong style={{ fontSize:13 }}>Signer {i+1}</strong>
          {signers.length > 1 && <Btn sz="xs" v="danger" onClick={()=>rm(i)}>× Remove</Btn>}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <Field label="Name *"><Input value={s.name} onChange={e=>upd(i,'name',e.target.value)}/></Field>
          <Field label="Role"><Input value={s.role} onChange={e=>upd(i,'role',e.target.value)} placeholder="Customer, Spouse, Crew Lead…"/></Field>
          <Field label="Email"><Input value={s.email} onChange={e=>upd(i,'email',e.target.value)} placeholder="they@example.com"/></Field>
          <Field label="Phone"><Input value={s.phone} onChange={e=>upd(i,'phone',e.target.value)} placeholder="(555) 555-5555"/></Field>
        </div>
      </div>
    ))}
    <Btn sz="sm" onClick={add} disabled={busy}>+ Add another signer</Btn>

    <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:14, fontSize:13, color:G.textMuted, cursor:'pointer' }}>
      <input type="checkbox" checked={notify} onChange={e=>setNotify(e.target.checked)}/>
      Notify signers now (SMS + email)
    </label>

    <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:14 }}>
      <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
      <Btn v="primary" onClick={send} disabled={busy}>{busy ? <Spinner sz={14}/> : '✍️ Send for Signature'}</Btn>
    </div>
  </Modal>;
}

// AuditModal — read-only audit trail + signer status. This is what the
// owner shows their lawyer/insurer if it's ever contested.
function AuditModal({ doc, onClose, toast }) {
  const [data, setData] = useState(null);
  useEffect(() => { api(`/api/documents/${doc.id}/audit`).then(setData).catch(e=>toast(e.message,'error')); }, [doc.id]);
  if (!data) return <Modal open onClose={onClose}><Spinner sz={24}/></Modal>;

  const copyLink = async (s) => {
    // Reconstruct the sign URL — we don't surface tokens on this endpoint for safety,
    // so we POST to create a fresh one. (Or alternately, surface them — keeping it
    // safe-by-default. For now: owner can re-send via /signers if needed.)
    toast('To re-send, click "✍️ Get Signed" and re-add this signer.');
  };

  return <Modal open onClose={onClose} width={680}>
    <h2 style={{ margin:'0 0 6px 0', fontSize:18 }}>🕓 Audit — {doc.title}</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>Complete record of every event. Defensible in court.</div>

    <div style={{ ...styles.card, padding:14, marginBottom:14 }}>
      <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:10 }}>Signers</div>
      {data.signers.length === 0 && <div style={{ fontSize:13, color:G.textMuted }}>No signers added yet. Click "✍️ Get Signed" on the document.</div>}
      {data.signers.map(s => (
        <div key={s.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:`1px solid ${G.border}` }}>
          <div>
            <div style={{ fontWeight:600 }}>{s.signer_name} {s.signer_role ? <span style={{ color:G.textMuted, fontSize:12 }}>· {s.signer_role}</span> : null}</div>
            <div style={{ fontSize:12, color:G.textMuted }}>{s.signer_email}{s.signer_phone ? ` · ${s.signer_phone}` : ''}</div>
            {s.signed_at && <div style={{ fontSize:11, color:G.green, marginTop:2 }}>✓ Signed {new Date(s.signed_at).toLocaleString()} · IP {s.ip || '—'}</div>}
            {s.declined_at && <div style={{ fontSize:11, color:G.red, marginTop:2 }}>✗ Declined {new Date(s.declined_at).toLocaleString()} {s.decline_reason?`· "${s.decline_reason}"`:''}</div>}
            {!s.signed_at && !s.declined_at && <div style={{ fontSize:11, color:G.yellow, marginTop:2 }}>⏳ Awaiting</div>}
          </div>
        </div>
      ))}
    </div>

    <div style={{ ...styles.card, padding:14 }}>
      <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:10 }}>Event Trail ({data.trail.length})</div>
      {data.trail.length === 0 ? <div style={{ fontSize:13, color:G.textMuted }}>No events yet.</div> :
        data.trail.slice().reverse().map((e, i) => (
          <div key={i} style={{ padding:'8px 0', borderBottom:`1px solid ${G.border}`, fontSize:13 }}>
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <strong>{e.event}</strong>
              <span style={{ fontSize:11, color:G.textMuted }}>{new Date(e.at).toLocaleString()}</span>
            </div>
            {(e.actor || e.ip) && <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>
              {e.actor ? `by ${e.actor}` : ''}{e.ip ? ` · IP ${e.ip}` : ''}
            </div>}
          </div>
        ))
      }
    </div>

    {data.fileUrl && <div style={{ marginTop:12 }}>
      <a href={data.fileUrl} target="_blank" rel="noreferrer" style={{ color:G.cyan, fontSize:13 }}>📎 Open attached file →</a>
    </div>}

    <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}>
      <Btn onClick={onClose}>Close</Btn>
    </div>
  </Modal>;
}

function DocumentModal({ doc, contacts, templates, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(doc||{}); }, [doc]);
  if (!doc) return null;
  const isNew = !f.id;
  const save = async () => {
    if (!f.title) return alert('Title required');
    try {
      if (isNew) await api('/api/documents', { method:'POST', body: f });
      else await api(`/api/documents/${f.id}`, { method:'PATCH', body: f });
      onSaved();
    } catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={760}>
    <h2 style={{ margin:'0 0 14px 0' }}>{isNew?'New Document':f.title}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Title *"><Input value={f.title||''} onChange={e=>setF({...f,title:e.target.value})}/></Field>
      <Field label="Type">
        <Select value={f.type||'agreement'} onChange={e=>setF({...f,type:e.target.value})}>
          <option>agreement</option><option>contract</option><option>form</option><option>quote</option>
        </Select>
      </Field>
      <Field label="Customer">
        <Select value={f.contactId||''} onChange={e=>setF({...f,contactId:e.target.value})}>
          <option value="">— select —</option>
          {contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
        </Select>
      </Field>
      <Field label="Template">
        <Select value={f.templateId||''} onChange={e=>{
          const t = templates.find(x=>x.id===e.target.value);
          setF({...f, templateId:e.target.value, content: t ? t.content : f.content});
        }}>
          <option value="">— none —</option>
          {templates.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
        </Select>
      </Field>
    </div>
    <Field label="Content">
      <Textarea value={f.content||''} onChange={e=>setF({...f,content:e.target.value})} style={{ minHeight:300, fontFamily:'monospace', fontSize:13 }}/>
    </Field>
    <div style={{ ...styles.card, padding:10, background:G.input, fontSize:12, color:G.textMuted, marginBottom:14 }}>
      💡 Use <code style={{color:G.cyan}}>{`{{client_name}}`}</code>, <code style={{color:G.cyan}}>{`{{address}}`}</code>, etc. as placeholders. They render in the customer's signing portal.
    </div>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}

function TemplateModal({ tpl, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(tpl||{}); }, [tpl]);
  if (!tpl) return null;
  const isNew = !f.id;
  const save = async () => {
    if (!f.title) return alert('Title required');
    try {
      if (isNew) await api('/api/documents/templates', { method:'POST', body: f });
      else await api(`/api/documents/templates/${f.id}`, { method:'PATCH', body: f });
      onSaved();
    } catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={760}>
    <h2 style={{ margin:'0 0 14px 0' }}>{isNew?'New Template':f.title}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:14 }}>
      <Field label="Title *"><Input value={f.title||''} onChange={e=>setF({...f,title:e.target.value})}/></Field>
      <Field label="Type"><Select value={f.type||'agreement'} onChange={e=>setF({...f,type:e.target.value})}><option>agreement</option><option>contract</option><option>form</option><option>quote</option></Select></Field>
    </div>
    <Field label="Template Body">
      <Textarea value={f.content||''} onChange={e=>setF({...f,content:e.target.value})} style={{ minHeight:300, fontFamily:'monospace', fontSize:13 }}/>
    </Field>
    <Field label="Variables (comma-separated)" hint="e.g. client_name, address, amount">
      <Input value={(f.variables||[]).join(', ')} onChange={e=>setF({...f, variables: e.target.value.split(',').map(s=>s.trim()).filter(Boolean)})}/>
    </Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}
